import React, { useEffect, useRef } from 'react';

import { graphql, useFragment } from 'react-relay';

import { useAnalytics } from '../../../analytics/use-analytics';
import type { Product } from '../../../types';
import type { GrowthRecContext } from './__generated__/freeToStandardModalQuery.graphql';
import type {
	twcSkuRecommendation_growthRecommendations$data,
	twcSkuRecommendation_growthRecommendations$key,
} from './__generated__/twcSkuRecommendation_growthRecommendations.graphql';

export const USE_CASE = 'twc-monetization-buy-crossflow';
export const SURFACE = 'edition-awareness';
export const TWC_ENTITY_ID = 'twc-collection';

const SUBPRODUCT: Partial<Record<Product, string>> = {
	jira: 'jira-software',
	confluence: 'confluence',
};

export type TwcSkuRecommendationStatus = 'success' | 'empty' | 'error';

type RankedRecommendation = {
	entityId: string;
	reasons: readonly string[];
	score: number | null;
};

export function buildTwcSkuRecommendationContext({
	cloudId,
	orgId,
	product,
	locale,
	recommendationSessionId,
}: {
	cloudId: string;
	locale: string;
	orgId?: string;
	product: Product;
	recommendationSessionId: string;
}): GrowthRecContext {
	return {
		tenantId: cloudId,
		orgId,
		product,
		subproduct: SUBPRODUCT[product],
		locale,
		sessionId: recommendationSessionId,
		useCase: USE_CASE,
		custom: { journey: 'upgrade', surface: SURFACE },
	};
}

export function TwcSkuRecommendationEvaluated({
	fragmentRef,
	recommendationSessionId,
	product,
}: {
	fragmentRef: twcSkuRecommendation_growthRecommendations$key | null | undefined;
	product: Product;
	recommendationSessionId: string | undefined;
}): React.JSX.Element | null {
	const { sendAnalyticsEvent } = useAnalytics();
	const data = useFragment(
		graphql`
			fragment twcSkuRecommendation_growthRecommendations on GrowthRecQuery
			@argumentDefinitions(
				context: { type: "GrowthRecContext" }
				first: { type: "Int", defaultValue: 2 }
			) {
				recommendations(context: $context, first: $first) {
					__typename
					... on GrowthRecRecommendations {
						context {
							custom
						}
						data {
							entityId
							reasons
							... on GrowthRecNonHydratedRecommendation {
								score
							}
						}
					}
					... on QueryError {
						extensions {
							errorType
							statusCode
						}
					}
				}
			}
		`,
		fragmentRef ?? null,
	);

	const loggedSessionId = useRef<string>();
	useEffect(() => {
		if (!recommendationSessionId || loggedSessionId.current === recommendationSessionId) {
			return;
		}
		loggedSessionId.current = recommendationSessionId;
		sendAnalyticsEvent({
			eventType: 'operational',
			action: 'evaluated',
			actionSubject: 'twcSkuRecommendation',
			attributes: {
				recommendationSessionId,
				useCase: USE_CASE,
				surface: SURFACE,
				...toAttributes(data, product),
			},
		});
	}, [recommendationSessionId, data, product, sendAnalyticsEvent]);

	return null;
}

function byScoreDescending(a: RankedRecommendation, b: RankedRecommendation): number {
	return (b.score ?? Number.NEGATIVE_INFINITY) - (a.score ?? Number.NEGATIVE_INFINITY);
}

function getModelVersion(custom: unknown): string | null {
	if (custom && typeof custom === 'object' && 'model_version' in custom) {
		const { model_version: modelVersion } = custom as { model_version?: unknown };
		return typeof modelVersion === 'string' ? modelVersion : null;
	}
	return null;
}

export function toAttributes(
	data: twcSkuRecommendation_growthRecommendations$data | null | undefined,
	product: Product,
): Record<string, unknown> & { status: TwcSkuRecommendationStatus } {
	const result = data?.recommendations;

	if (result?.__typename === 'QueryError') {
		const extension = result.extensions?.[0];
		return {
			status: 'error',
			errorType: extension?.errorType ?? null,
			statusCode: extension?.statusCode ?? null,
		};
	}

	if (result?.__typename !== 'GrowthRecRecommendations' || !result.data?.length) {
		return { status: 'empty' };
	}

	const recommendations: RankedRecommendation[] = result.data
		.map((item) => ({
			entityId: item.entityId,
			score: item.score ?? null,
			reasons: item.reasons ?? [],
		}))
		.sort(byScoreDescending);

	const [top] = recommendations;
	const standaloneEntityId = SUBPRODUCT[product] ?? product;

	return {
		status: 'success',
		recommendedSku: top.entityId,
		isTwcRecommended: top.entityId === TWC_ENTITY_ID,
		twcScore: recommendations.find((r) => r.entityId === TWC_ENTITY_ID)?.score ?? null,
		standaloneScore:
			recommendations.find((r) => r.entityId === standaloneEntityId || r.entityId === product)
				?.score ?? null,
		modelVersion: getModelVersion(result.context?.custom),
		recommendations,
	};
}
